import { apiService } from "../services/axios";
import { useEffect, useState } from 'react';
import { useAuth } from '../provider/AuthProvider';
import { ARRAN_WAR_CATEGORY_CODE, ARRAN_WAR_CROSSOVER_CATEGORY_CODE } from '../utils/categoryTranslations';
import iconBook from '../assets/icon-book.png';
import { VIEW_MODES } from '../utils/viewMode';

const ComicList = ({ categoryCode, viewMode = VIEW_MODES.mosaic }) => {
    const [comics, setComics] = useState([]);
    const [myComicsIds, setMyComicsIds] = useState([]);
    const { token, isReady } = useAuth();

    const addComicToCollection = async (comicId) => {
        try {
            await apiService.post('/api/me/comics', { comicId }, isReady, token);
            setMyComicsIds(prev => [...prev, comicId]);
        } catch (error) {
            console.error('Erreur lors de l\'ajout du comic:', error);
            alert('Erreur lors de l\'ajout du comic à votre collection');
        }
    };

    const removeComicFromCollection = async (comicId) => {
        try {
            await apiService.delete(`/api/me/comics/${comicId}`, isReady, token);
            setMyComicsIds(prev => prev.filter(id => id !== comicId));
        } catch (error) {
            console.error('Erreur lors de la suppression du comic:', error);
            alert('Erreur lors de la suppression du comic de votre collection');
        }
    };

    useEffect(() => {
        apiService.get('/api/comics', isReady, token)
            .then((response) => {
                setComics(response.data)
            })
            .catch((error) => {
                console.log(error);
            });
    }, [isReady, token]);

    useEffect(() => {
        apiService.get('/api/me/comics', isReady, token)
            .then((response) => {
                setMyComicsIds(response.data.map((comic) => comic.comicId));
            })
            .catch((error) => {
                console.log(error);
            });
    }, [isReady, token]);

    const comicsByCategory = comics.reduce((groups, comic) => {
        const comicCategoryCode = comic.mainCategory.code;
        const isArranWarCrossoverFilter = categoryCode === ARRAN_WAR_CROSSOVER_CATEGORY_CODE;
        const isInFilteredCategory = comicCategoryCode === categoryCode;
        const isCrossoverForArranWar = isArranWarCrossoverFilter && comic.belongsToArranWarCrossover;

        // Si un categoryCode est spécifié : catégorie stricte, sauf pour "Guerres d'Arran (crossover)" qui inclut les crossovers
        if (categoryCode !== null && categoryCode !== undefined && !isInFilteredCategory && !isCrossoverForArranWar) {
            return groups;
        }

        const groupKey = isCrossoverForArranWar ? ARRAN_WAR_CATEGORY_CODE : comicCategoryCode;
        const groupName = groupKey === ARRAN_WAR_CATEGORY_CODE && comicCategoryCode !== ARRAN_WAR_CATEGORY_CODE
            ? "Guerres d'Arran"
            : comic.mainCategory.name;

        if (!groups[groupKey]) {
            groups[groupKey] = {
                name: groupName,
                comics: []
            };
        }
        groups[groupKey].comics.push(comic);
        return groups;
    }, {});

    if (categoryCode === ARRAN_WAR_CROSSOVER_CATEGORY_CODE && comicsByCategory[ARRAN_WAR_CATEGORY_CODE]) {
        comicsByCategory[ARRAN_WAR_CATEGORY_CODE].comics.sort((a, b) =>
            (b.releaseDate || '').localeCompare(a.releaseDate || '')
        );
    }

    const classeFond = (comicId) => myComicsIds.includes(comicId)
        ? 'has-background-success-light'
        : 'has-background-danger-light';

    // Même bouton dans les deux vues : l'action ne dépend pas de la mise en page.
    const boutonCollection = (comicId) => myComicsIds.includes(comicId)
        ? (
            <button className="button" onClick={() => removeComicFromCollection(comicId)}>
                Enlever de ma collection &#x1F5D1;
            </button>
        )
        : (
            <button className="button" onClick={() => addComicToCollection(comicId)}>
                Ajouter à ma collection &#x1F4B8;
            </button>
        );

    const complementsTitre = (comic) => (
        <>
            {comic.editionName !== null && <><br/>{comic.editionName}</>}
            {comic.belongsToArranWarCrossover &&
                <><br/><strong><span className="icon"><i className="fas fa-globe"></i></span> Guerres d'Arran</strong></>
            }
        </>
    );

    const vueMosaique = (comics) => (
        <div className="fixed-grid grid has-3-cols has-1-cols-mobile">
            <div className="grid">
                {comics.map((comic) => (
                    <div key={comic.id} className={"card large " + classeFond(comic.id)}
                        data-category-code={comic.mainCategory.code}
                        data-category-name={comic.mainCategory.name}
                    >
                        <div className="card-image">
                            <figure className="image">
                                <img src={comic.imageUrl} alt={comic.title} />
                            </figure>
                        </div>
                        <div className="card-content">
                            <div className="media">
                                <div className="media-left">
                                    <figure className="image is-48x48">
                                        <img src={iconBook} alt="Book icone" />
                                    </figure>
                                </div>
                                <div className="media-content">
                                    <p className="title is-4 no-padding">{comic.title}</p>
                                    <p>
                                        <span className="title is-6">{comic.mainCategory.name} #{comic.volumeNumber}</span>
                                    </p>
                                    <p className="subtitle is-6">
                                        Paru le {comic.releaseDateFormatted}
                                        {complementsTitre(comic)}
                                    </p>
                                    <div>{boutonCollection(comic.id)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // Vue liste : une ligne par album, couverture à gauche, action à droite.
    // Les colonnes Bulma s'empilent d'elles-mêmes sur mobile, où l'horizontale
    // n'aurait pas la place de tenir.
    const vueListe = (comics) => (
        <div>
            {comics.map((comic) => (
                <div key={comic.id}
                    className={"comic-row columns is-vcentered p-2 " + classeFond(comic.id)}
                    data-category-code={comic.mainCategory.code}
                    data-category-name={comic.mainCategory.name}
                >
                    <div className="column is-2">
                        {/* La hauteur est bornée dans App.css : .image img impose
                            width:100%, qui rendrait la limite inopérante. */}
                        <figure className="image comic-row-cover">
                            <img src={comic.imageUrl} alt={comic.title} />
                        </figure>
                    </div>
                    <div className="column">
                        <p className="title is-5 mb-1">{comic.title}</p>
                        <p className="subtitle is-6 mb-1">
                            {comic.mainCategory.name} #{comic.volumeNumber}
                        </p>
                        <p className="is-size-7">
                            Paru le {comic.releaseDateFormatted}
                            {complementsTitre(comic)}
                        </p>
                    </div>
                    <div className="column is-narrow">
                        {boutonCollection(comic.id)}
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div>
            {Object.entries(comicsByCategory).map(([categoryCode, categoryData]) => (
                <div key={categoryCode}>
                    <h2 className="title is-3 p-4 mb-4">
                        {categoryData.name}
                    </h2>

                    {viewMode === VIEW_MODES.list
                        ? vueListe(categoryData.comics)
                        : vueMosaique(categoryData.comics)}
                </div>
            ))}
        </div>
    );
}

export default ComicList;
